import React, { FC, useEffect, useState } from 'react';
import { message, Skeleton } from 'antd';
import {
  MdOutlineKeyboardArrowRight,
  MdOutlineKeyboardArrowLeft,
} from 'react-icons/md';
import Axios from 'axios';
import Carousel from 'nuka-carousel';
import { ToogleLinkButton } from 'app/components/ToogleLinkButton/ToogleLinkButton';
import { BUTTON } from 'app/components/ToogleLinkButton/models.d';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { getSlider } from 'app/adapters/Slider';
import { SliderI } from 'app/adapters/Slider/models.d';
import { TemplatePageI } from 'app/adapters/TemplatePages';
import { InsightsI } from 'app/adapters/Insights';
import { sanitizedContent } from 'app/utils/sanitizedContent';
import './SliderCarousel.scss';

const SliderCarousel: FC = () => {
  const { xs, sm } = useBreakpoint();
  const [sliders, setSliders] = useState<SliderI[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getSliderData = async () => {
    try {
      const { data } = await getSlider().finally(() => {
        setIsLoading(false);
      });
      setSliders(data.results);
    } catch (error) {
      if (!Axios.isCancel(error)) {
        message.error('An error occurred while fetching the slider data');
      }
    }
  };

  useEffect(() => {
    getSliderData();
  }, []);

  const renderBtn = (
    internal_page: TemplatePageI | null,
    internal_insight: InsightsI | null,
    external_url: string | null,
    text: string,
  ) => (
    <div className="slider-carousel__btn-wrapper">
      {internal_page || internal_insight ? (
        <ToogleLinkButton
          version={BUTTON.VERSION.INTERNAL_LINK}
          link={
            internal_page
              ? `/${internal_page.slug}`
              : internal_insight
              ? `/insights/${internal_insight.page_url}`
              : ''
          }
          variant={BUTTON.VARIANT.OUTLINE}
          size={BUTTON.SIZE.BIG}
          round={BUTTON.ROUND.MEDIUM}
          color={BUTTON.COLOR.WHITE}
        >
          {text}
        </ToogleLinkButton>
      ) : external_url ? (
        <ToogleLinkButton
          version={BUTTON.VERSION.EXTERNAL_LINK}
          link={external_url}
          variant={BUTTON.VARIANT.OUTLINE}
          size={BUTTON.SIZE.BIG}
          round={BUTTON.ROUND.MEDIUM}
          color={BUTTON.COLOR.WHITE}
        >
          {text}
        </ToogleLinkButton>
      ) : null}
    </div>
  );
  return (
    <Carousel
      defaultControlsConfig={{
        pagingDotsStyle: {
          display: sliders?.length > 1 ? 'block' : 'none',
          width: sm ? '120px' : '70px',
          height: sm ? '14px' : '8px',
          margin: '0 3px 10px',
          background: 'white',
          color: 'transparent',
          fill: 'white',
        },
      }}
      renderCenterLeftControls={({ previousSlide }) => (
        <div
          className="slider-carousel__arrow-wrapper"
          role="button"
          onClick={previousSlide}
        >
          <MdOutlineKeyboardArrowLeft className="slider-carousel__arrow" />
        </div>
      )}
      renderCenterRightControls={({ nextSlide }) => (
        <div
          className="slider-carousel__arrow-wrapper"
          role="button"
          onClick={nextSlide}
        >
          <MdOutlineKeyboardArrowRight
            onClick={nextSlide}
            className="slider-carousel__arrow"
          />
        </div>
      )}
      wrapAround
      dragging={sliders?.length > 1}
      autoplay={sliders?.length > 1}
      autoplayInterval={10000}
      pauseOnHover={false}
    >
      {!isLoading ? (
        sliders?.map(
          (
            {
              name,
              content,
              image,
              img_alt,
              internal_page,
              internal_insight,
              external_url,
              button_text,
            },
            index,
          ) => (
            <div key={index} className="slider-carousel">
              {image ? (
                <img
                  src={image}
                  alt={img_alt ?? ''}
                  className="slider-carousel__img"
                  onDragStart={e => {
                    e.preventDefault();
                    return false;
                  }}
                />
              ) : null}
              <div
                dangerouslySetInnerHTML={sanitizedContent(
                  '<h1>' + name + '</h1>',
                )}
                className="slider-carousel__title-wrapper"
              />
              <p className="slider-carousel__description" dangerouslySetInnerHTML={sanitizedContent(content)}></p>
              {renderBtn(
                internal_page,
                internal_insight,
                external_url,
                button_text,
              )}
            </div>
          ),
        )
      ) : (
        <Skeleton
          active
          paragraph={false}
          title={{
            style: { height: xs ? '520px' : '538px', width: '100%' },
          }}
        />
      )}
    </Carousel>
  );
};
export default SliderCarousel;
